import React, { useState, useMemo, useEffect, useCallback } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import toast from 'react-hot-toast';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  useFetchPropertyQuery,
  useDeletePropertyIncidentAdminMutation,
  usePatchPropertyIncidentAdminMutation,
} from '@@/services/property';
import { useFetchMessagesQuery } from '@@/services/message';
import { useFetchIncidentsQuery } from '@@/services/incident';
import { useFetchAuthorizedRoomsQuery } from '@@/services/room';
import {
  openAddIncidentAdminModal,
  openAddAuthorizedGroupModal,
} from '@@/features/modal';
import { Building2, Copy, Calendar, Trash2, Plus } from 'lucide-react';
import { IncidentStatuses } from '@@/constants/incident';
import AddIncidentAdminModal from '@@/components/Modals/AddIncidentAdminModal';
import AddAuthorizedGroupModal from '@@/components/Modals/AddAuthorizedGroupModal';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import EditableCell from '@@/components/Table/EditableCell';

const columnHelper = createColumnHelper<
  Alacaza.PropertyAdmin & {
    isVisible: boolean;
    showContactInfo: boolean;
    enableNotifications: boolean;
    enableReporting: boolean;
  }
>();

enum Tabs {
  INFO = 'info',
  CODES = 'codes',
  MESSAGES = 'messages',
  INCIDENTS = 'incidents',
  ADMINS = 'admins',
  AUTHORIZED_ROOMS = 'authorized_rooms',
}

const getTabTitle = (tab: Tabs) => {
  switch (tab) {
    case Tabs.INFO:
      return 'Informations générales';
    case Tabs.CODES:
      return 'Codes';
    case Tabs.MESSAGES:
      return 'Messages';
    case Tabs.INCIDENTS:
      return 'Incidents';
    case Tabs.ADMINS:
      return 'Gestionnaires';
    case Tabs.AUTHORIZED_ROOMS:
      return 'Groupes autorisés';
    default:
      return tab;
  }
};

const Tab: React.FC<{
  id: Tabs;
  currentTab: Tabs;
  cb: (arg: Tabs) => void;
}> = ({ id, currentTab, cb }) => {
  const isActive = useMemo(() => currentTab === id, [currentTab, id]);

  return (
    <button
      className={cx(
        'inline-block px-6 py-3 border-b-2 border-transparent rounded-t-lg transition-colors duration-200 text-body-sm font-medium',
        isActive
          ? 'bg-primary-600 text-white border-primary-600'
          : 'text-neutral-600 hover:text-neutral-900 hover:border-neutral-300',
      )}
      onClick={() => {
        window.location.hash = id;
        cb(id);
      }}
    >
      {getTabTitle(id)}
    </button>
  );
};

const Message: React.FC<{ message: Alacaza.ScheduledMessage }> = ({
  message,
}) => {
  return (
    <Link
      to={`/messages/${message.id}`}
      className="block my-3 p-4 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg transition-colors"
    >
      <p className="text-body text-neutral-900 truncate mb-2">
        {message.postId ? '📊 Sondage' : message.message}
      </p>
      <div className="flex items-center text-neutral-500">
        <Calendar size={14} className="mr-2" />
        <p className="text-body-xs italic">
          {moment(message.sentAt || message.scheduledAt).format('lll')}
        </p>
      </div>
    </Link>
  );
};

const Incident: React.FC<{ incident: Alacaza.Incident }> = ({ incident }) => {
  return (
    <Link
      to={`/incidents/${incident.id}`}
      className="block my-3 p-4 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg transition-colors"
    >
      <p className="font-semibold text-body text-neutral-900 mb-2">{incident.title}</p>
      <p className="text-body-sm text-neutral-600 truncate mb-2">
        {incident.description}
      </p>
      <p className="text-body-xs text-neutral-500">
        Rapporté par : {incident.creator.firstName}&nbsp;
        {incident.creator.lastName}&nbsp;le&nbsp;
        {moment(incident.createdAt).format('lll')}
      </p>
    </Link>
  );
};

const PropertyPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { propertyId } = useParams();
  const { data: property } = useFetchPropertyQuery(propertyId || '', {
    skip: !propertyId,
  });
  const { data: messages } = useFetchMessagesQuery(
    { propertyId, page: 1, limit: 500 },
    {
      skip: !propertyId,
    },
  );
  const { data: incidents } = useFetchIncidentsQuery(
    { propertyId },
    {
      skip: !propertyId,
    },
  );
  const { data: rooms } = useFetchAuthorizedRoomsQuery(propertyId || '', {
    skip: !propertyId,
  });

  const [deletePropertyAdmin, { isSuccess: deleteIsSuccess }] =
    useDeletePropertyIncidentAdminMutation();

  const [patchPropertyAdmin, { isSuccess: patchIsSuccess }] =
    usePatchPropertyIncidentAdminMutation();

  const showAddIncidentAdminModal = useCallback(() => {
    dispatch(openAddIncidentAdminModal());
  }, [dispatch]);

  const showAddAuthorizedGroupModal = useCallback(() => {
    dispatch(openAddAuthorizedGroupModal());
  }, [dispatch]);

  useEffect(() => {
    if (deleteIsSuccess) {
      toast.success('Le gestionnaire a bien été supprimé', {
        position: 'top-right',
      });
    }
  }, [deleteIsSuccess]);

  useEffect(() => {
    if (patchIsSuccess) {
      toast.success('Le gestionnaire a bien été modifié', {
        position: 'top-right',
      });
    }
  }, [patchIsSuccess]);

  const [tab, setTab] = useState(Tabs.INFO);
  useEffect(() => {
    if (location?.hash) {
      setTab(location.hash.replace('#', '') as Tabs);
    }
  }, [location?.hash, setTab]);

  const data = useMemo(() => {
    if (!property?.admins) {
      return [];
    }

    return property.admins.map((admin) => ({
      ...admin,
      isVisible: !admin.isPrivate,
      showContactInfo: !admin.isPrivate && !admin.hideContactInfo,
      enableNotifications: !admin.disableNotifications,
      enableReporting: !admin.disableReporting,
    }));
  }, [property?.admins]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('firstName', {
        header: 'Prénom',
        cell: EditableCell,
        meta: {
          type: 'text',
          width: 'w-24',
        },
      }),
      columnHelper.accessor('lastName', {
        header: 'Nom',
        cell: EditableCell,
        meta: {
          type: 'text',
          width: 'w-24',
        },
      }),
      columnHelper.accessor('jobTitle', {
        header: 'Fonction',
        cell: EditableCell,
        meta: {
          type: 'text',
          width: 'w-24',
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: EditableCell,
        meta: {
          type: 'text',
          width: 'w-40',
        },
      }),
      columnHelper.accessor('phoneNumber', {
        header: 'Tél.',
        cell: EditableCell,
        meta: {
          type: 'phone',
        },
      }),
      columnHelper.accessor('isVisible', {
        header: 'Visible',
        cell: EditableCell,
        meta: {
          type: 'checkbox',
        },
      }),
      columnHelper.accessor((row) => row, {
        header: 'Coord.',
        id: 'showContactInfo',
        cell: ({ getValue, row, column, table }) => {
          const { isVisible, showContactInfo, id } = getValue();
          return (
            <EditableCell
              getValue={() => showContactInfo}
              row={row}
              column={column}
              table={table}
              locked={!isVisible}
              customKey={`${id}-${!isVisible}`}
            />
          );
        },
        meta: {
          type: 'checkbox',
        },
      }),
      columnHelper.accessor('enableNotifications', {
        header: 'Notif.',
        cell: EditableCell,
        meta: {
          type: 'checkbox',
        },
      }),
      columnHelper.accessor('enableReporting', {
        header: 'Report.',
        cell: EditableCell,
        meta: {
          type: 'checkbox',
        },
      }),
      columnHelper.accessor((row) => row, {
        id: 'deletion',
        header: '',
        cell: (row) => {
          const { id } = row.getValue();

          return (
            <button
              onClick={() => {
                if (propertyId) {
                  deletePropertyAdmin({ propertyId, propertyAdminId: id });
                }
              }}
              className="inline-flex items-center p-1 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-button transition-colors"
            >
              <Trash2 size={18} />
            </button>
          );
        },
      }),
    ],
    [deletePropertyAdmin, propertyId],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: (id: string, columnId: string, value: string | boolean) => {
        const body: Record<string, string | boolean> = {};

        switch (columnId) {
          case 'isVisible':
            body.isPrivate = !value;
            if (body.isPrivate) {
              body.hideContactInfo = true;
            }
            break;
          case 'showContactInfo':
            body.hideContactInfo = !value;
            break;
          case 'enableNotifications':
            body.disableNotifications = !value;
            break;
          case 'enableReporting':
            body.disableReporting = !value;
            break;
          default:
            body[columnId] = value;
            break;
        }

        if (propertyId) {
          patchPropertyAdmin({
            propertyId,
            propertyAdminId: id,
            body,
          });
        }
      },
    },
  });

  const latestSentMessages = useMemo(() => {
    return messages?.docs.filter((m) => m.sentAt).slice(0, 3);
  }, [messages?.docs]);

  const latestScheduledMessages = useMemo(() => {
    return messages?.docs.filter((m) => !m.sentAt).slice(0, 3);
  }, [messages?.docs]);

  const latestIncidentsInProgress = useMemo(() => {
    return incidents?.docs
      .filter((i) => i.status === IncidentStatuses.IN_PROGRESS)
      .slice(0, 5);
  }, [incidents?.docs]);

  const latestClosedIncidents = useMemo(() => {
    return incidents?.docs
      .filter((i) => i.status !== IncidentStatuses.IN_PROGRESS)
      .slice(0, 10);
  }, [incidents?.docs]);

  const tabContent = useMemo(() => {
    if (!property) {
      return null;
    }

    switch (tab) {
      case Tabs.INFO:
        return (
          <div className="space-y-6">
            <div>
              <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Nom de la résidence</p>
              <p className="text-body text-neutral-900">{property.name}</p>
            </div>

            <div>
              <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Quartier</p>
              <p className="text-body text-neutral-900">{property.zoneName}</p>
            </div>

            <div>
              <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Adresse(s)</p>
              <div className="space-y-2">
                {property.units.map((unit, i) => (
                  <p className="text-body text-neutral-900" key={i}>
                    {unit.address.fullAddress}
                  </p>
                ))}
              </div>
            </div>

            <div>
              <p className="text-body-sm font-medium text-neutral-500 uppercase tracking-wide mb-2">Bâtiment(s)</p>
              <div className="space-y-2">
                {property.buildings.map((building, i) => (
                  <p className="text-body text-neutral-900" key={i}>
                    {building.name}
                  </p>
                ))}
              </div>
            </div>
          </div>
        );
      case Tabs.CODES:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {property.codes?.map((code, i) => (
              <div key={i} className="bg-neutral-50 border border-neutral-200 rounded-lg p-4">
                <div className="inline-flex items-center px-3 py-1.5 bg-warning-400 text-neutral-900 rounded-badge text-body font-semibold mb-3">
                  {code.code}
                </div>
                <p className="text-body-xs text-neutral-500 italic mb-3">
                  Valide du {moment(code.validFrom).format('ll')}&nbsp;au&nbsp;
                  {moment(code.validUntil).format('ll')}
                </p>
                <button
                  className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded-button text-body-sm text-neutral-700 hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-colors duration-200 w-full justify-center"
                  onClick={() => {
                    navigator.clipboard.writeText(code.shortLink || code.url);
                    toast.success("L'url a bien été copiée", {
                      position: 'top-right',
                    });
                  }}
                >
                  <Copy size={16} />
                  <span>Copier l'url</span>
                </button>
              </div>
            ))}
          </div>
        );
      case Tabs.MESSAGES:
        return (
          <div className="space-y-8">
            <div>
              <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
                DERNIERS MESSAGES PROGRAMMÉS
              </p>
              <div className="space-y-2">
                {latestScheduledMessages?.map((m, i) => (
                  <Message key={i} message={m} />
                ))}
              </div>
              {!!latestScheduledMessages?.length ? (
                <Link
                  to={`/messages?propertyId=${propertyId}&status=scheduled`}
                  className="inline-flex items-center text-body-sm text-primary-600 hover:text-primary-700 font-medium mt-4 underline"
                >
                  Voir tous les messages programmés
                </Link>
              ) : (
                <p className="text-body-sm text-neutral-500 mt-4">Aucun message programmé</p>
              )}
            </div>
            <hr className="border-neutral-200" />
            <div>
              <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
                DERNIERS MESSAGES ENVOYÉS
              </p>
              <div className="space-y-2">
                {latestSentMessages?.map((m, i) => (
                  <Message key={i} message={m} />
                ))}
              </div>
              {!!latestSentMessages?.length ? (
                <Link
                  to={`/messages?propertyId=${propertyId}&status=sent`}
                  className="inline-flex items-center text-body-sm text-primary-600 hover:text-primary-700 font-medium mt-4 underline"
                >
                  Voir tous les messages envoyés
                </Link>
              ) : (
                <p className="text-body-sm text-neutral-500 mt-4">Aucun message envoyé</p>
              )}
            </div>
          </div>
        );
      case Tabs.INCIDENTS:
        return (
          <div className="space-y-8">
            <div>
              <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
                DERNIERS INCIDENTS EN COURS
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {latestIncidentsInProgress?.map((incident, i) => (
                  <Incident key={i} incident={incident} />
                ))}
              </div>

              {!!latestIncidentsInProgress?.length ? (
                <Link
                  to={`/incidents?propertyId=${propertyId}&status=${IncidentStatuses.IN_PROGRESS}`}
                  className="inline-flex items-center text-body-sm text-primary-600 hover:text-primary-700 font-medium mt-4 underline"
                >
                  Voir tous les incidents en cours
                </Link>
              ) : (
                <p className="text-body-sm text-neutral-500 mt-4">Aucun incident en cours</p>
              )}
            </div>

            <hr className="border-neutral-200" />

            <div>
              <p className="text-body-sm font-semibold text-neutral-500 uppercase tracking-wide mb-4">
                DERNIERS INCIDENTS TRAITÉS
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {latestClosedIncidents?.map((incident, i) => (
                  <Incident key={i} incident={incident} />
                ))}
              </div>
              {!!latestClosedIncidents?.length ? (
                <Link
                  to={`/incidents?propertyId=${propertyId}&status=${IncidentStatuses.RESOLVED}`}
                  className="inline-flex items-center text-body-sm text-primary-600 hover:text-primary-700 font-medium mt-4 underline"
                >
                  Voir tous les incidents traités
                </Link>
              ) : (
                <p className="text-body-sm text-neutral-500 mt-4">Aucun incident traité</p>
              )}
            </div>
          </div>
        );
      case Tabs.ADMINS:
        return (
          <div>
            {!!propertyId && <AddIncidentAdminModal propertyId={propertyId} />}
            <div className="flex justify-end mb-6">
              <button
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-button shadow-button transition-colors duration-200"
                onClick={() => {
                  showAddIncidentAdminModal();
                }}
              >
                <Plus size={20} />
                <span className="text-body-sm">Ajouter un gestionnaire</span>
              </button>
            </div>

            {/* Mobile Cards - visible < lg */}
            <div className="lg:hidden space-y-4">
              {data.map((admin) => (
                <div
                  key={admin.id}
                  className="bg-white rounded-card shadow-card border border-neutral-200 p-4 hover:shadow-card-hover transition-shadow"
                >
                  <div className="mb-3">
                    <p className="font-semibold text-neutral-900 text-body mb-1">
                      {admin.firstName} {admin.lastName}
                    </p>
                    {admin.jobTitle && (
                      <p className="text-neutral-600 text-body-sm mb-2">{admin.jobTitle}</p>
                    )}
                    <p className="text-neutral-600 text-body-sm mb-1">{admin.email}</p>
                    {admin.phoneNumber?.internationalNumber && (
                      <p className="text-neutral-600 text-body-sm">{admin.phoneNumber.internationalNumber}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {admin.isVisible && (
                        <span className="inline-flex items-center px-2 py-1 rounded-badge text-body-xs font-medium bg-success-100 text-success-800">
                          Visible
                        </span>
                      )}
                      {admin.enableNotifications && (
                        <span className="inline-flex items-center px-2 py-1 rounded-badge text-body-xs font-medium bg-info-100 text-info-800">
                          Notif.
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        if (propertyId) {
                          deletePropertyAdmin({ propertyId, propertyAdminId: admin.id });
                        }
                      }}
                      className="inline-flex items-center p-2 text-danger-600 hover:text-danger-700 hover:bg-danger-50 rounded-button transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))}

              {data.length === 0 && (
                <div className="bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
                  <p className="text-body text-neutral-500">Aucun gestionnaire trouvé</p>
                </div>
              )}
            </div>

            {/* Desktop Table (éditable) - visible >= lg */}
            <div className="hidden lg:block overflow-x-auto bg-white rounded-card shadow-card border border-neutral-200">
              <table className="w-full table-auto">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-2 py-2 text-center text-body-xs font-semibold text-white bg-neutral-700 border border-neutral-200 whitespace-nowrap"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-neutral-50 transition-colors">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-1 py-1.5 text-center border-b border-neutral-200">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case Tabs.AUTHORIZED_ROOMS:
        return (
          <div>
            {!!propertyId && (
              <AddAuthorizedGroupModal propertyId={propertyId} />
            )}
            <div className="flex justify-end mb-6">
              <button
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-button shadow-button transition-colors duration-200"
                onClick={() => {
                  showAddAuthorizedGroupModal();
                }}
              >
                <Plus size={20} />
                <span className="text-body-sm">Ajouter un groupe autorisé</span>
              </button>
            </div>

            {!!rooms?.length && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {rooms?.map((room: Alacaza.Room) => (
                  <div
                    key={room.id}
                    className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-body font-semibold text-neutral-900 mb-1">{room.title}</h3>
                    <p className="text-body-sm italic text-neutral-600 mb-2">{room.description}</p>
                    <p className="text-body-xs italic text-neutral-500">
                      {room.invitedUserIds?.length} membre(s)
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  }, [
    tab,
    property,
    latestScheduledMessages,
    latestSentMessages,
    latestClosedIncidents,
    latestIncidentsInProgress,
    propertyId,
    rooms,
  ]);

  if (!property) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center">
              <Building2 size={28} className="text-neutral-900" />
              <h1 className="text-heading-lg text-neutral-900 ml-3">{property.name}</h1>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-t-card shadow-sm border-b border-neutral-200">
            <ul className="flex flex-wrap">
              <Tab id={Tabs.INFO} currentTab={tab} cb={setTab} />
              <Tab id={Tabs.CODES} currentTab={tab} cb={setTab} />
              <Tab id={Tabs.MESSAGES} currentTab={tab} cb={setTab} />
              <Tab id={Tabs.INCIDENTS} currentTab={tab} cb={setTab} />
              <Tab id={Tabs.ADMINS} currentTab={tab} cb={setTab} />
              <Tab id={Tabs.AUTHORIZED_ROOMS} currentTab={tab} cb={setTab} />
            </ul>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-b-card shadow-card border border-neutral-200 border-t-0 p-6">
            {tabContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyPage;
